/**
 * DrawingOverlay — geo-referenced freehand drawing canvas.
 *
 * Strokes are stored as { color, width, points: [[lng, lat], …] }.
 * On every map move/zoom/rotate/resize event, each point is reprojected
 * via map.project([lng, lat]) so drawings stay locked to map features.
 *
 * Exposed via forwardRef: { clear(), undo() }
 */

import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from "react";

const DrawingOverlay = forwardRef(function DrawingOverlay(
  { map, active, color = "#ef4444", strokeWidth = 3 },
  ref,
) {
  const canvasRef    = useRef(null);
  const strokesRef   = useRef([]);   // committed strokes
  const currentRef   = useRef(null); // stroke in progress
  const isDrawingRef = useRef(false);

  // ── Expose clear / undo to parent ────────────────────────────────────────
  useImperativeHandle(ref, () => ({
    clear() {
      strokesRef.current = [];
      renderFn();
    },
    undo() {
      strokesRef.current = strokesRef.current.slice(0, -1);
      renderFn();
    },
  }));

  // ── Core render ───────────────────────────────────────────────────────────
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const renderFn = useCallback(function render() {
    const canvas = canvasRef.current;
    if (!canvas || !map) return;

    const dpr = window.devicePixelRatio || 1;
    const ctx  = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.save();
    ctx.scale(dpr, dpr);

    const all = [...strokesRef.current, currentRef.current].filter(Boolean);

    for (const stroke of all) {
      if (!stroke.points || stroke.points.length < 2) continue;

      ctx.beginPath();
      ctx.strokeStyle = stroke.color;
      ctx.lineWidth   = stroke.width;
      ctx.lineCap     = "round";
      ctx.lineJoin    = "round";

      const first = map.project(stroke.points[0]);
      ctx.moveTo(first.x, first.y);

      for (let i = 1; i < stroke.points.length; i++) {
        const px = map.project(stroke.points[i]);
        ctx.lineTo(px.x, px.y);
      }
      ctx.stroke();
    }

    ctx.restore();
  }, [map]); // map is stable after init

  // ── DPR-aware resize ──────────────────────────────────────────────────────
  const resizeFn = useCallback(function resize() {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width  = canvas.offsetWidth  * dpr;
    canvas.height = canvas.offsetHeight * dpr;
    renderFn();
  }, [renderFn]);

  // ResizeObserver — keeps canvas dimensions in sync with element
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ro = new ResizeObserver(resizeFn);
    ro.observe(canvas);
    resizeFn(); // initial size
    return () => ro.disconnect();
  }, [resizeFn]);

  // ── Map event subscriptions ───────────────────────────────────────────────
  useEffect(() => {
    if (!map) return;
    map.on("move",   renderFn);
    map.on("zoom",   renderFn);
    map.on("rotate", renderFn);
    map.on("resize", resizeFn);
    return () => {
      map.off("move",   renderFn);
      map.off("zoom",   renderFn);
      map.off("rotate", renderFn);
      map.off("resize", resizeFn);
    };
  }, [map, renderFn, resizeFn]);

  // ── Pointer input ─────────────────────────────────────────────────────────
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !active || !map) return;

    function getCoords(e) {
      const rect = canvas.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      const ll = map.unproject([x, y]);
      return [ll.lng, ll.lat];
    }

    function onDown(e) {
      e.preventDefault();
      canvas.setPointerCapture(e.pointerId);
      isDrawingRef.current = true;
      currentRef.current = {
        color,
        width: strokeWidth,
        points: [getCoords(e)],
      };
      renderFn();
    }

    function onMove(e) {
      if (!isDrawingRef.current) return;
      e.preventDefault();
      currentRef.current.points.push(getCoords(e));
      renderFn();
    }

    function onUp(e) {
      if (!isDrawingRef.current) return;
      e.preventDefault();
      isDrawingRef.current = false;
      if (currentRef.current && currentRef.current.points.length > 1) {
        strokesRef.current = [...strokesRef.current, currentRef.current];
      }
      currentRef.current = null;
      renderFn();
    }

    canvas.addEventListener("pointerdown",   onDown);
    canvas.addEventListener("pointermove",   onMove);
    canvas.addEventListener("pointerup",     onUp);
    canvas.addEventListener("pointercancel", onUp);

    return () => {
      canvas.removeEventListener("pointerdown",   onDown);
      canvas.removeEventListener("pointermove",   onMove);
      canvas.removeEventListener("pointerup",     onUp);
      canvas.removeEventListener("pointercancel", onUp);
    };
  }, [active, color, strokeWidth, map, renderFn]);

  // ── Canvas element ────────────────────────────────────────────────────────
  return (
    <canvas
      ref={canvasRef}
      style={{
        position:      "absolute",
        top:           0,
        left:          0,
        width:         "100%",
        height:        "100%",
        zIndex:        20,
        pointerEvents: active ? "all" : "none",
        touchAction:   active ? "none" : "auto",
        cursor:        active ? "crosshair" : "default",
      }}
    />
  );
});

export default DrawingOverlay;
